import { HUMANS, scenarios } from '../data/populations';
import { Scenario } from '../types/census';

export type ParityOutcome =
  | { kind: 'crossed'; at: number }
  | { kind: 'future'; at: number }
  | { kind: 'never' };

/**
 * Linear crossover: machines(t) = M0 + a·t, humans(t) = H0 + b·t, both from the same dated base.
 * Parity when M0 + a·t = H0 + b·t  →  t = (H0 − M0) / (a − b).
 */
export function parityFor(m0: number, a: number, h0 = HUMANS.base, b = HUMANS.ratePerSec, base = HUMANS.baseTime): ParityOutcome {
  if (m0 >= h0) return { kind: 'crossed', at: base - ((m0 - h0) / Math.max(1e-9, a - b)) * 1000 };
  if (a <= b) return { kind: 'never' };
  return { kind: 'future', at: base + ((h0 - m0) / (a - b)) * 1000 };
}

export interface ScenarioParity {
  scenario: Scenario;
  central: ParityOutcome;
  high: ParityOutcome;
}

export function scenarioParity(s: Scenario): ScenarioParity {
  return { scenario: s, central: parityFor(s.count, s.ratePerSec), high: parityFor(s.high, s.ratePerSec * 2) };
}

export const allParity = scenarios.map(scenarioParity);

export function ratioAt(s: Scenario, t: number): number {
  const dt = Math.max(0, t - HUMANS.baseTime) / 1000;
  return (s.count + s.ratePerSec * dt) / (HUMANS.base + HUMANS.ratePerSec * dt);
}

export function yearLabel(o: ParityOutcome): string {
  if (o.kind === 'never') return 'not on this trajectory';
  return new Date(o.at).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
